import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { logout } from '../../../slices/authSlice';
import ICN from './pageIcons';
import Views from './Views';
import Toolbar from './Toolbar';
import TableView from './TableView';
import BoardView from './BoardView';
import TimelineView from './TimelineView';
import BulkBar from './BulkBar';
import { statusFor, targetCodes, progressOf } from './projectHelpers';

const PAGE_SIZE = 25;

const FACET_CYCLES = {
    language: ['Any', 'DE', 'FR', 'JA', 'ES'],
    status: ['Any', 'Live', 'In review', 'Blocked', 'Queued', 'Done'],
    owner: ['Anyone', 'Me'],
    due: ['Anytime', 'Today', 'This week', 'This month', 'Overdue'],
};

const STATUS_LABEL_TO_KEY = {
    Live: 'live',
    'In review': 'review',
    Blocked: 'blocked',
    Queued: 'queued',
    Done: 'done',
};

const AllProjects = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const token = useSelector((s) => s.auth.token);
    const me = useSelector((s) => s.auth.user) || {};

    const [allProjects, setAllProjects] = useState([]);
    const [activeView, setActiveView] = useState('All');
    const [q, setQ] = useState('');
    const [view, setView] = useState('table');
    const [facets, setFacets] = useState({ language: 'Any', status: 'Any', owner: 'Anyone', due: 'Anytime' });
    const [selected, setSelected] = useState({});
    const [page, setPage] = useState(0);
    const [sort, setSort] = useState('created');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchProjects = (archivedParam) => {
        const headers = { Authorization: `Bearer ${token}` };
        const arch = archivedParam ? `&archived=${archivedParam}` : '';
        return axios.get(`/api/projects?summary=true${arch}`, { headers }).then((r) => {
            const { createdProjects = [], sharedProjects = [] } = r.data || {};
            const unique = new Map([...createdProjects, ...sharedProjects].map((p) => [p._id, p]));
            return [...unique.values()];
        });
    };

    useEffect(() => {
        if (!token) return;
        setLoading(true);
        fetchProjects(activeView === 'Archived' ? 'true' : null)
            .then((projects) => {
                setAllProjects(projects);
                setLoading(false);
            })
            .catch((err) => {
                if (err.response && err.response.status === 401) {
                    dispatch(logout());
                    navigate('/login');
                } else {
                    setError('Failed to load projects.');
                    setLoading(false);
                }
            });
    }, [token, activeView, dispatch, navigate]);

    const toggleFacet = (k) => setFacets((f) => {
        const cycle = FACET_CYCLES[k];
        const i = cycle.indexOf(f[k] || cycle[0]);
        return { ...f, [k]: cycle[(i + 1) % cycle.length] };
    });
    const clearFacets = () => setFacets({ language: 'Any', status: 'Any', owner: 'Anyone', due: 'Anytime' });

    const filteredProjects = useMemo(() => {
        const now = Date.now();
        const dayMs = 1000 * 60 * 60 * 24;
        let r = allProjects.slice();
        if (activeView === 'Mine') {
            r = r.filter((p) => (p.owner === me._id || p.createdBy === me._id));
        } else if (activeView === 'In review') {
            r = r.filter((p) => statusFor(p) === 'review');
        } else if (activeView === 'Late') {
            r = r.filter((p) => p.deadline && new Date(p.deadline).getTime() < now && !p.completed);
        } else if (activeView === 'Done') {
            r = r.filter((p) => p.completed || p.status === 'completed');
        }
        const s = q.trim().toLowerCase();
        if (s) {
            r = r.filter((p) => `${p.name} ${p.category || ''}`.toLowerCase().includes(s));
        }
        if (facets.language !== 'Any') {
            r = r.filter((p) => targetCodes(p).includes(facets.language));
        }
        if (facets.status !== 'Any') {
            const key = STATUS_LABEL_TO_KEY[facets.status];
            if (key) r = r.filter((p) => statusFor(p) === key);
        }
        if (facets.owner === 'Me') {
            r = r.filter((p) => p.owner === me._id || p.createdBy === me._id);
        }
        if (facets.due !== 'Anytime') {
            r = r.filter((p) => {
                if (!p.deadline) return false;
                const diff = (new Date(p.deadline).getTime() - now) / dayMs;
                if (facets.due === 'Today') return diff >= 0 && diff < 1;
                if (facets.due === 'This week') return diff >= 0 && diff <= 7;
                if (facets.due === 'This month') return diff >= 0 && diff <= 31;
                if (facets.due === 'Overdue') return diff < 0;
                return true;
            });
        }
        if (sort === 'created') {
            r.sort((a, b) => {
                const ad = new Date(a.createdAt || 0).getTime();
                const bd = new Date(b.createdAt || 0).getTime();
                return bd - ad; // newest first
            });
        } else if (sort === 'due') {
            r.sort((a, b) => {
                if (!a.deadline) return 1;
                if (!b.deadline) return -1;
                return new Date(a.deadline) - new Date(b.deadline);
            });
        } else if (sort === 'name') {
            r.sort((a, b) => (a.projectName || a.name || '').localeCompare(b.projectName || b.name || ''));
        } else if (sort === 'progress') {
            r.sort((a, b) => progressOf(b) - progressOf(a));
        }
        return r;
    }, [allProjects, activeView, q, facets, sort, me._id]);

    const viewCounts = useMemo(() => {
        const now = Date.now();
        return {
            All: allProjects.length,
            Mine: allProjects.filter((p) => p.owner === me._id || p.createdBy === me._id).length,
            'In review': allProjects.filter((p) => statusFor(p) === 'review').length,
            Late: allProjects.filter((p) => p.deadline && new Date(p.deadline).getTime() < now && !p.completed).length,
            Done: allProjects.filter((p) => p.completed || p.status === 'completed').length,
            Archived: allProjects.filter((p) => p.archivedAt).length,
        };
    }, [allProjects, me._id]);

    const viewItems = [
        { id: 'All', label: 'All', count: viewCounts.All },
        { id: 'Mine', label: 'Mine', count: viewCounts.Mine },
        { id: 'In review', label: 'In review', count: viewCounts['In review'] },
        { id: 'Late', label: 'Late', count: viewCounts.Late },
        { id: 'Done', label: 'Done · 30d', count: viewCounts.Done },
        { id: 'Archived', label: 'Archived', count: viewCounts.Archived },
    ];

    const paged = useMemo(
        () => filteredProjects.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE),
        [filteredProjects, page],
    );

    const toggleSel = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
    const toggleAll = () => {
        const allOn = paged.length > 0 && paged.every((r) => selected[r._id]);
        const next = { ...selected };
        paged.forEach((r) => { next[r._id] = !allOn; });
        setSelected(next);
    };
    const clearSel = () => setSelected({});
    const selectedIds = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
    const selCount = selectedIds.length;

    const bulkArchive = async () => {
        if (!selectedIds.length) return;
        const headers = { Authorization: `Bearer ${token}` };
        try {
            await axios.post('/api/projects/bulk-archive', { ids: selectedIds, archive: true }, { headers });
            setAllProjects((arr) => arr.map((p) => (selectedIds.includes(p._id) ? { ...p, archivedAt: new Date().toISOString() } : p)));
            setSelected({});
        } catch (e) {
            console.error('Bulk archive failed', e);
        }
    };

    const bulkExport = () => {
        const lines = ['name,deadline,status,progress'];
        selectedIds.forEach((id) => {
            const p = allProjects.find((x) => x._id === id);
            if (p) {
                lines.push(`"${(p.name || '').replace(/"/g, '""')}",${p.deadline || ''},${statusFor(p)},${progressOf(p)}`);
            }
        });
        const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'projects-export.csv';
        a.click();
        URL.revokeObjectURL(url);
    };

    if (loading) {
        return <div style={{ padding: 40, color: 'var(--ink-3, #5b5b62)' }}>Loading projects…</div>;
    }
    if (error) {
        return <div style={{ padding: 40, color: 'crimson' }}>{error}</div>;
    }

    return (
        <>
            <div className="rs-page-title-row">
                <h1 className="rs-page-title">All projects</h1>
                <span className="rs-page-title-count">{viewCounts.All}</span>
                <div className="rs-page-actions">
                    <button type="button" className="btn-q" onClick={bulkExport}>{ICN.download}<span>Export</span></button>
                    <button type="button" className="btn-q">{ICN.filter}<span>Filters</span></button>
                    <button type="button" className="btn-p" onClick={() => navigate('/dashboard/new-translation')}>
                        {ICN.plus}<span>New project</span>
                    </button>
                </div>
            </div>
            <p className="rs-page-sub">
                Everything across your workspace. Translate, review, and ship from one table.
            </p>

            <Views items={viewItems} active={activeView} setActive={(v) => { setActiveView(v); setPage(0); }} />

            <Toolbar
                q={q}
                setQ={(v) => { setQ(v); setPage(0); }}
                view={view}
                setView={setView}
                facets={facets}
                toggleFacet={(k) => { toggleFacet(k); setPage(0); }}
                clearFacets={clearFacets}
                sort={sort}
                setSort={setSort}
            />

            {view === 'table' && (
                <TableView
                    rows={paged}
                    allRowsCount={filteredProjects.length}
                    selected={selected}
                    toggleSel={toggleSel}
                    toggleAll={toggleAll}
                    page={page}
                    setPage={setPage}
                    pageSize={PAGE_SIZE}
                />
            )}
            {view === 'board' && <BoardView rows={filteredProjects} />}
            {view === 'timeline' && <TimelineView rows={filteredProjects} />}

            {filteredProjects.length === 0 && (
                <div className="rs-card" style={{ padding: 64, textAlign: 'center', border: '1.5px dashed var(--line-2)', background: 'transparent' }}>
                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" style={{ margin: '0 auto 10px', display: 'block', color: 'var(--ink-4)' }}>
                        <path d="M5 11a1 1 0 011-1h6l2 2h12a1 1 0 011 1v11a1 1 0 01-1 1H6a1 1 0 01-1-1V11z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
                    </svg>
                    <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 4 }}>Nothing here yet</div>
                    <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>
                        Try a different view, clear filters, or start a new project.
                    </div>
                </div>
            )}

            {selCount > 0 && (
                <BulkBar
                    count={selCount}
                    onArchive={bulkArchive}
                    onExport={bulkExport}
                    onClear={clearSel}
                    onTag={() => {}}
                    onAssign={() => {}}
                />
            )}
        </>
    );
};

export default AllProjects;
